import { z } from 'zod'
import { imageAssetRefSchema } from '../helpers/asset-ref.js'
import { focalPointSchema } from '../helpers/focal-point.js'
import { mediaKindOf, onSite } from '../helpers/vendor-media.js'
import { emailSchema } from '../validators/email.validator.js'
import { phoneSchema } from '../validators/phone.validator.js'
import {
  MAX_VENDOR_MEDIA_LINKS,
  MAX_VENDOR_PHOTOS,
  MAX_VENDOR_PHOTO_CAPTION,
  PROFILE_URL_MAX,
  VENDOR_DESCRIPTION_MAX,
} from '../constants/vendor-profile.js'

/**
 * A vendor's profile, as the company writes it (docs/vendor-growth.spec.md):
 * what it says about itself, how to reach it, its gallery and its video and
 * audio links. Its packages and items are `vendor-offering.input.ts`.
 */

/**
 * A field that can be cleared: an empty (or blank) box is the same as null, so
 * a person who deletes their phone number clears it rather than being told it
 * is too short.
 */
export function clearable<T extends z.ZodType<string>>(schema: T) {
  // Blank after trimming, not only empty: a box of spaces clears too.
  return z
    .union([z.string().trim().length(0).transform(() => null), schema])
    .nullable()
    .optional()
}

const http = (v: string): boolean => /^https?:\/\//i.test(v)

/** The company's own website. */
export const websiteSchema = z
  .string()
  .trim()
  .max(PROFILE_URL_MAX)
  .url('Use the full address, like https://example.com')
  .refine(http, 'Use the full address, like https://example.com')

/**
 * The company's page on a social site. A bare handle (`@spincity`) is refused
 * rather than guessed at: the same handle can be a different account per site.
 */
export function socialUrlSchema(label: string, example: string, domains: readonly string[]) {
  const message = `Use your ${label} address, like ${example}`
  return z
    .string()
    .trim()
    .max(PROFILE_URL_MAX)
    .url(message)
    .refine((v) => onSite(v, domains), message)
}

export const instagramUrlSchema = socialUrlSchema('Instagram', 'https://instagram.com/yourname', ['instagram.com'])
export const facebookUrlSchema = socialUrlSchema('Facebook', 'https://facebook.com/yourname', ['facebook.com', 'fb.com'])
export const tiktokUrlSchema = socialUrlSchema('TikTok', 'https://tiktok.com/@yourname', ['tiktok.com'])
export const pinterestUrlSchema = socialUrlSchema('Pinterest', 'https://pinterest.com/yourname', ['pinterest.com', 'pin.it'])

/**
 * The profile's text and contact fields, as `PATCH /vendor` takes them beside
 * the company's name and address. Each is optional; null or an empty box
 * clears it.
 */
export const vendorProfileFieldsSchema = {
  description: clearable(z.string().trim().max(VENDOR_DESCRIPTION_MAX, `Keep it to ${String(VENDOR_DESCRIPTION_MAX)} characters`)),
  website: clearable(websiteSchema),
  phone: clearable(phoneSchema),
  publicEmail: clearable(emailSchema),
  instagramUrl: clearable(instagramUrlSchema),
  facebookUrl: clearable(facebookUrlSchema),
  tiktokUrl: clearable(tiktokUrlSchema),
  pinterestUrl: clearable(pinterestUrlSchema),
}

const caption = z.string().trim().max(MAX_VENDOR_PHOTO_CAPTION)

/** Add a photo to the gallery. The caption is its alt text, so it is asked for. */
export const addVendorPhotoInputSchema = z
  .object({
    image: imageAssetRefSchema,
    caption: caption.nullable().optional(),
    focalPoint: focalPointSchema.nullable().optional(),
  })
  .strict()

export type AddVendorPhotoInput = z.infer<typeof addVendorPhotoInputSchema>

/** Change a photo's caption or focal point; the image itself is replaced by removing it and adding another. */
export const updateVendorPhotoInputSchema = z
  .object({
    caption: caption.nullable().optional(),
    focalPoint: focalPointSchema.nullable().optional(),
  })
  .strict()

export type UpdateVendorPhotoInput = z.infer<typeof updateVendorPhotoInputSchema>

/** The gallery's whole order, every time (as a room's photos). */
export const reorderVendorPhotosInputSchema = z
  .object({
    photoIds: z
      .array(z.string().uuid())
      .min(1, 'Send the photos in the order they should appear.')
      .max(MAX_VENDOR_PHOTOS)
      .refine((ids) => new Set(ids).size === ids.length, 'Each photo can only appear once.'),
  })
  .strict()

export type ReorderVendorPhotosInput = z.infer<typeof reorderVendorPhotosInputSchema>

/** One video or audio link. Which site it is on is worked out from the address. */
export const vendorMediaLinkInputSchema = z
  .object({
    url: z
      .string()
      .trim()
      .max(PROFILE_URL_MAX)
      .refine((v) => mediaKindOf(v) !== null, 'Link a YouTube, Vimeo or SoundCloud page'),
    title: z.string().trim().max(120).nullable().optional(),
  })
  .strict()

export type VendorMediaLinkInput = z.infer<typeof vendorMediaLinkInputSchema>

/** The whole list of links, in order: there are few, and the list is what is on screen. */
export const putVendorMediaLinksInputSchema = z
  .object({
    links: z.array(vendorMediaLinkInputSchema).max(MAX_VENDOR_MEDIA_LINKS, `Up to ${String(MAX_VENDOR_MEDIA_LINKS)} links`),
  })
  .strict()

export type PutVendorMediaLinksInput = z.infer<typeof putVendorMediaLinksInputSchema>
